import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { fetchAPI } from "@/lib/api";

interface Circuit {
  id: number;
  title: string;
  description: string;
  image_url: string;
}

export const SpiritualPaths = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [circuits, setCircuits] = useState<Circuit[]>([]);

  useEffect(() => {
    fetchAPI("?type=circuits")
      .then((data: any) => {
        setCircuits(data.slice(0, 6));
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => {
                el.classList.add("visible");
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [circuits]);

  if (circuits.length === 0) return null;

  return (
    <section ref={sectionRef} className="py-24 lg:py-32 bg-indigo text-primary-foreground">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="reveal text-gold text-sm tracking-[0.2em] uppercase font-medium">
            Our Spiritual Circuits
          </span>
          <h2 className="reveal font-serif text-3xl md:text-4xl lg:text-5xl mt-4 mb-6">
            Six Paths to <span className="text-gradient-sacred">Inner Discovery</span>
          </h2>
          <p className="reveal text-primary-foreground/70 leading-relaxed">
            Each circuit offers a unique doorway to transformation. 
            Choose the path that calls to your heart, or let us guide you.
          </p>
        </div>

        {/* Circuits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {circuits.map((circuit, index) => (
            <Link
              key={circuit.id}
              to="/circuits"
              className="reveal group"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="relative h-72 rounded-2xl overflow-hidden">
                {/* Image */}
                <img 
                  src={`https://baelleaves.com/${circuit.image_url}`}
                  alt={circuit.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-indigo via-indigo/50 to-transparent" />

                {/* Content */}
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-serif text-xl mb-2 group-hover:text-gold transition-colors">
                        {circuit.title}
                      </h3>
                      <p className="text-primary-foreground/70 text-sm max-w-xs line-clamp-2">
                        {circuit.description}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center group-hover:bg-gold group-hover:text-indigo transition-all duration-300 shrink-0">
                      <ArrowUpRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
